import type { ProductCompetitorService } from '@shared/types/product-competitor-service';
import type { CompetitorService } from '@shared/types/competitor-service';

/// 商材詳細「競合ソリューション」エディタの純粋ロジック。
///
/// 保存先は `product_competitor_services` (= `自社ソリューション x 外部ソリューション` の競合関係)。
/// この商材が競合として設定している外部ソリューション (competitor_service) を 1 行とし、
/// 表示名・未紐付け状態・追加候補の絞り込みを UI から切り離して単体テストする。
///
/// 「外部ソリューション詳細」起点の競合タブ (`CompetitorProductsEditor`) は read-only 表示
/// のみでロジックを持たない。こちらは「商材詳細」起点 (この商材の競合として紐づく
/// 外部ソリューション一覧) の編集ロジック。

/** Linked rows use the current master name; legacy NULL rows use their saved title. */
export function productCompetitorDisplayName(
  row: ProductCompetitorService,
): string {
  // During the new-FE/old-BE rolling window the old response has no
  // competitor_service_name. Keep the saved title fallback until that
  // compatibility window is retired.
  const value =
    row.competitor_service_id === null
      ? row.title
      : (row.competitor_service_name ?? row.title);
  return value?.trim() ?? '';
}

/** A legacy relation card whose master ID has not been resolved. */
export function isUnlinkedProductCompetitor(
  row: ProductCompetitorService,
): boolean {
  return row.competitor_service_id === null;
}

/** Number shown by the editor's `未紐付け N件` indicator. */
export function countUnlinkedProductCompetitors(
  rows: readonly ProductCompetitorService[],
): number {
  return rows.filter(isUnlinkedProductCompetitor).length;
}

/**
 * Candidates for the current product's add modal.
 *
 * Global `has_competitor_relation` is deliberately ignored: a service linked
 * to another product remains a valid candidate here. Only IDs already linked
 * to this product are excluded.
 */
export function filterProductCompetitorCandidates(
  services: readonly CompetitorService[],
  rows: readonly ProductCompetitorService[],
  query: string,
): CompetitorService[] {
  const linkedIds = new Set(
    rows.flatMap((row) =>
      row.competitor_service_id === null ? [] : [row.competitor_service_id],
    ),
  );
  const normalizedQuery = query.trim().toLocaleLowerCase('ja');

  return services
    .filter((service) => service.kind === 'competitor')
    .filter((service) => !linkedIds.has(service.id))
    .filter(
      (service) =>
        normalizedQuery === '' ||
        service.name.toLocaleLowerCase('ja').includes(normalizedQuery),
    )
    .sort((a, b) => {
      const byName = a.name.localeCompare(b.name, 'ja');
      return byName !== 0 ? byName : a.id.localeCompare(b.id);
    });
}
